import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { SEARCH_PAGE_SIZE } from "./constants";
import {
  CohereTable,
  Column,
  Chip,
  SingleSelect,
  Body2,
  H1,
  H3,
  getCaseRedirectUrl,
  ReassignModal,
  UnassignModal,
  colorsLight,
  SelectOptionsHook,
  useLazyLoadingQueryOptionsHook,
  PaginateProps,
} from "@coherehealth/common";
import NoResultsClipboardIcon from "components/images/NoResultsClipboardIcon";
import { ServiceCase, User, useSearchUser, useUpdateServiceCaseAssignee } from "@coherehealth/qm-api";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import { useSnackbar } from "notistack";
import { getSearchColumns, COLUMN_SORT_KEYS } from "./searchColumns";
import { UserCtx } from "./fieldRegistry";

interface SearchResultsTableProps {
  results: ServiceCase[];
  totalCount: number;
  loading: boolean;
  userCtx: UserCtx;
  healthPlanLabels?: Record<string, string>;
  searchVersion: number;
  canReassign: boolean;
  onSearchParamsChange: (params: { page: number; max: number; sortBy?: string[]; sortDesc?: boolean }) => void;
  onAssigneeUpdated: (updated: ServiceCase) => void;
}

export default function SearchResultsTable({
  results,
  totalCount,
  loading,
  userCtx,
  healthPlanLabels,
  searchVersion,
  canReassign,
  onSearchParamsChange,
  onAssigneeUpdated,
}: SearchResultsTableProps) {
  const { enqueueSnackbar } = useSnackbar();
  // CohereTable convention: paginateParams.max = displayMax + 1 (one over the visible page size);
  // displayMax = max - 1. All arithmetic below and in the paginateParams effect uses this invariant.
  const [paginateParams, setPaginateParams] = useState<PaginateProps>({ max: SEARCH_PAGE_SIZE + 1, offset: 0 });
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDescending, setSortDescending] = useState(false);

  const [openAssigneeCaseId, setOpenAssigneeCaseId] = useState<string | null>(null);
  const [selectedServiceCase, setSelectedServiceCase] = useState<ServiceCase | null>(null);
  const [agent, setAgent] = useState<User>({ name: "" });
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);

  // Ref so the optionsDecorator inside useAssigneeOptions always reads the latest
  // selectedServiceCase without the hook factory needing to be in useMemo deps.
  const selectedServiceCaseRef = useRef<ServiceCase | null>(null);
  selectedServiceCaseRef.current = selectedServiceCase;

  // Mirrors SupervisorDashboard's useUsers exactly: plain arrow function, same hook shape,
  // supportOnly:false, and optionsDecorator that prepends "Unassign" when the case has an
  // existing assignee and the query is empty.
  // selectedServiceCase is read via ref so this function doesn't need to be in useMemo deps
  // (it would be a new reference every render, causing columns to recompute every render,
  // which remounts SingleSelect every render and resets the dropdown list height to 0).
  const useAssigneeOptions: SelectOptionsHook<User> = (selectOptionsParams) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useLazyLoadingQueryOptionsHook({
      ...selectOptionsParams,
      useGetHook: useSearchUser,
      additionalQueryParams: { name: selectOptionsParams.query, supportOnly: false },
      optionsDecorator: (opts) => {
        const query = selectOptionsParams.query;
        if (selectedServiceCaseRef.current?.assignee?.name) {
          return opts && opts.length > 0 ? (query === "" ? [{ name: "Unassign" } as User, ...opts] : opts) : [];
        }
        return opts;
      },
    });

  const { mutate: updateAssignee, loading: reassignLoading } = useUpdateServiceCaseAssignee({});

  const baseColumns = useMemo(() => getSearchColumns(userCtx, healthPlanLabels), [userCtx, healthPlanLabels]);

  const columns: Column<ServiceCase>[] = useMemo(() => {
    return baseColumns.map((col) => {
      if (col.name !== "assignee") {
        return col;
      }
      return {
        ...col,
        value: (sc: ServiceCase) => {
          // Use sc.id (always populated) not sc.caseNumber as the discriminator:
          // caseNumber can be absent, and "" would match multiple rows simultaneously.
          const caseId = sc.id ?? "";
          const assigneeName = sc.assignee?.name || "Unassigned";

          if (openAssigneeCaseId === caseId) {
            return (
              // Stops mouse clicks inside the picker from bubbling to the table row's onClick
              // handler and triggering case navigation. No role/onKeyDown needed: this div is not
              // an interactive element for users — it is purely a pointer-event boundary. Keyboard
              // navigation is protected independently: onRowClick (below) already guards against
              // navigation when openAssigneeCaseId is set, so all paths are covered.
              <div onClick={(e) => e.stopPropagation()}>
                <SingleSelect
                  clearable
                  id={caseId}
                  markSelectedOptions={false}
                  placeholder="Search agents"
                  useOptions={useAssigneeOptions}
                  selectedValue={agent}
                  getOptionLabel={(option: User) => option.name || ""}
                  setSelectedValue={(option: User | null) => {
                    if (option === null || option.name === "Unassign") {
                      setAgent({ name: "" });
                      setSelectedServiceCase(sc);
                      setShowUnassignModal(true);
                      return;
                    }
                    setAgent(option);
                    setSelectedServiceCase(sc);
                    setShowReassignModal(true);
                  }}
                  openOnFocus
                  onClose={() => setOpenAssigneeCaseId(null)}
                  // CRITICAL: do NOT add inputProps here. SingleSelect's renderInput spreads
                  // {...params} first then {...TextFieldProps} last, so any inputProps in
                  // TextFieldProps completely replaces params.inputProps — which contains MUI
                  // Autocomplete's onFocus/onBlur handlers. Losing those breaks openOnFocus and
                  // the dropdown never opens even though the API call succeeds.
                  TextFieldProps={{ autoFocus: true, style: { height: "40px" } }}
                />
              </div>
            );
          }

          // PATCH /serviceCase/assignee is SUPERVISOR-only; render plain non-clickable chip
          // for reviewer roles to avoid a 403 on submit and an empty user picker on open.
          return (
            <Chip
              clickable={canReassign}
              style={{
                minWidth: "120px",
                maxWidth: "180px",
                margin: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              label={assigneeName}
              type="default"
              customBackgroundColor={colorsLight.primary.faint20}
              customLabelColor={colorsLight.primary.active}
              aria-label={
                canReassign
                  ? assigneeName === "Unassigned"
                    ? "Assign agent"
                    : `Change assignee: ${assigneeName}`
                  : `Assignee: ${assigneeName}`
              }
              onClick={
                canReassign
                  ? (event: React.MouseEvent) => {
                      event.stopPropagation();
                      setAgent({ name: "" });
                      setSelectedServiceCase(sc);
                      setOpenAssigneeCaseId(caseId);
                    }
                  : undefined
              }
              Icon={canReassign ? KeyboardArrowDown : undefined}
              size="small"
            />
          );
        },
      };
    });
    // useState setters are intentionally excluded: React guarantees stable identity.
    // `agent` IS included: drives the value shown in the open SingleSelect as the user types.
    // `useAssigneeOptions` is intentionally EXCLUDED: it is a new function reference every render
    // (plain arrow function). Including it would recompute columns every render, remounting
    // SingleSelect every render and resetting LazyLoadingOptions height to 0 on every keystroke.
    // selectedServiceCase is read via selectedServiceCaseRef inside useAssigneeOptions so the
    // decorator always has the current value without needing to be in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseColumns, openAssigneeCaseId, agent, canReassign]);

  const prevPaginateRef = useRef(paginateParams);
  // Both refs suppress the paginateParams effect when its triggering setPaginateParams call was
  // already accompanied by a direct onSearchParamsChange call elsewhere in the same handler.
  // Two separate refs (rather than one) so each caller clears its own flag independently.
  const sortJustFiredRef = useRef(false); // set by handleSortChange before it calls setPaginateParams
  const searchJustFiredRef = useRef(false); // set by the searchVersion effect before it calls setPaginateParams
  useEffect(() => {
    const prev = prevPaginateRef.current;
    prevPaginateRef.current = paginateParams;
    if (sortJustFiredRef.current) {
      sortJustFiredRef.current = false;
      return;
    }
    if (searchJustFiredRef.current) {
      searchJustFiredRef.current = false;
      return;
    }
    if (prev.max !== paginateParams.max || prev.offset !== paginateParams.offset) {
      const displayMax = (paginateParams.max ?? SEARCH_PAGE_SIZE + 1) - 1;
      onSearchParamsChange({
        page: Math.floor((paginateParams.offset ?? 0) / displayMax),
        max: displayMax,
        sortBy: sortColumn ? [COLUMN_SORT_KEYS[sortColumn] ?? sortColumn] : undefined,
        sortDesc: sortDescending,
      });
    }
    // onSearchParamsChange intentionally excluded: stable in practice (useCallback in parent) but
    // adding it would re-run this effect whenever the parent's callback identity changes, causing
    // spurious searches. sortColumn/sortDescending intentionally excluded: the sort path fires
    // onSearchParamsChange directly in handleSortChange (suppressing this effect via sortJustFiredRef)
    // so this effect must not re-run on sort state changes. When pagination fires after a sort,
    // sortColumn/sortDescending are already committed state and carry the correct current sort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginateParams]);

  const handleSortChange = useCallback(
    (columnName: string, ascending: boolean) => {
      const beField = COLUMN_SORT_KEYS[columnName] ?? columnName;
      const newDesc = !ascending;
      sortJustFiredRef.current = true; // suppress paginateParams effect: setPaginateParams (next line) triggers it, but onSearchParamsChange is called directly below
      // Store the UI column name (not the BE field) so CohereTable's active-arrow comparison
      // (column.name === sortingColumnName) works correctly for columns with remapped sort keys.
      setSortColumn(columnName);
      setSortDescending(newDesc);
      setPaginateParams((prev) => ({ ...prev, offset: 0 }));
      onSearchParamsChange({
        page: 0,
        max: (paginateParams.max ?? SEARCH_PAGE_SIZE + 1) - 1,
        sortBy: [beField],
        sortDesc: newDesc,
      });
    },
    [paginateParams.max, onSearchParamsChange]
  );

  const handleRowClick = useCallback(
    (sc: ServiceCase) => {
      const url = getCaseRedirectUrl({
        caseId: sc.id,
        caseType: sc.caseType,
        serviceRequestId: sc.serviceRequest?.serviceRequestId,
        faxId: sc.faxDetail?.attachmentId,
        // fromSupervisorDashboard routes review types to /view_only_review instead of the
        // createNew*Review path, which would create a draft review on open.
        fromSupervisorDashboard: true,
      });
      const isSafeScheme = url?.startsWith("https://") || url?.startsWith("http://") || url?.startsWith("/");
      if (url && !url.includes("/undefined") && isSafeScheme) {
        window.location.assign(url);
      } else {
        enqueueSnackbar("Unable to open this case. Please refresh and try again.", { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  const handleReassign = useCallback(async () => {
    if (!selectedServiceCase || !agent.name?.trim()) {
      enqueueSnackbar("No agent selected. Please search and select an agent to reassign.", { variant: "warning" });
      return;
    }
    try {
      const updated = await updateAssignee({
        serviceCaseId: selectedServiceCase.id,
        assignee: { name: agent.name, userName: agent.username },
        assignmentMethodDetails: { assignmentMethod: "MANUAL_ASSIGNMENT" },
      });
      onAssigneeUpdated(updated);
      setShowReassignModal(false);
      setOpenAssigneeCaseId(null);
      setAgent({ name: "" });
    } catch {
      // Intentionally keep the modal open so the user can retry without reopening the picker.
      enqueueSnackbar("Failed to reassign case.", { variant: "error" });
    }
  }, [selectedServiceCase, agent, updateAssignee, onAssigneeUpdated, enqueueSnackbar]);

  const handleUnassign = useCallback(async () => {
    if (!selectedServiceCase) {
      return;
    }
    try {
      const updated = await updateAssignee({
        serviceCaseId: selectedServiceCase.id,
        assignee: { name: undefined, userName: undefined },
        assignmentMethodDetails: { assignmentMethod: "MANUAL_ASSIGNMENT" },
      });
      onAssigneeUpdated(updated);
      setShowUnassignModal(false);
      setOpenAssigneeCaseId(null);
      setAgent({ name: "" });
    } catch {
      // Intentionally keep the modal open so the user can retry.
      enqueueSnackbar("Failed to unassign case.", { variant: "error" });
    }
  }, [selectedServiceCase, updateAssignee, onAssigneeUpdated, enqueueSnackbar]);

  useEffect(() => {
    // The ref write MUST precede setPaginateParams in the same effect body. JavaScript is
    // single-threaded: React cannot schedule the paginateParams effect until this effect body
    // returns, so the ref is already true by the time that effect reads it. This follows from
    // JS execution semantics, not from React's commit-phase sequencing.
    // Reset max (not just offset) so the table's per-page state stays consistent with the
    // handleSearch call that already fired with max:SEARCH_PAGE_SIZE. Preserving a non-default
    // max here would leave the per-page selector showing e.g. "25" while only 10 results arrived.
    // Reset sort state so the column header indicator matches what the API actually returned:
    // handleSearch sends no sortBy/sortDesc, so the API uses its default sort order. Without this
    // reset a stale sort arrow would show after re-search even though results are unsorted.
    searchJustFiredRef.current = true;
    setPaginateParams({ max: SEARCH_PAGE_SIZE + 1, offset: 0 });
    setSortColumn(undefined);
    setSortDescending(false);
  }, [searchVersion]);

  return (
    <div data-testid="search-results-table" role="status" aria-busy={loading} style={{ position: "relative" }}>
      <Body2
        style={{
          color: colorsLight.font.light,
          fontWeight: 400,
          marginBottom: 8,
        }}
      >
        {/* Suppress "0 results" during in-flight load so the live region doesn't mislead screen readers */}
        {!loading && `${totalCount} ${totalCount === 1 ? "result" : "results"}`}
      </Body2>
      <CohereTable<ServiceCase>
        tableColumns={columns}
        data={results}
        loading={loading}
        noTextWrap
        paginateViaParams
        paginateParams={paginateParams}
        setPaginateParams={setPaginateParams}
        displayMax={(paginateParams.max ?? SEARCH_PAGE_SIZE + 1) - 1}
        totalCount={totalCount}
        onRowClick={(sc: ServiceCase) => {
          // Block row navigation while the assignee picker is open for any row.
          // The stopPropagation wrapper on the picker div handles clicks inside the
          // dropdown, but CohereTable's own row-click handler fires on the TR element
          // which is outside that div. Guard here so clicking the row background while
          // the picker is open doesn't navigate away.
          if (openAssigneeCaseId !== null) {
            return;
          }
          handleRowClick(sc);
        }}
        onSortChange={handleSortChange}
        sortingColumnName={sortColumn}
        sortingAscending={!sortDescending}
        customTableRowPadding="12px 24px"
        headerMargin="32px 0 10px 0"
        hideFinalDivider
        rowHoverShadow="0px 4px 8px 0px rgba(21, 21, 21, 0.16)"
        hideRowHoverBorderColor
        useCustomDisplayForEmptyContainer={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 80,
              padding: "120px 80px",
              border: `1px solid ${colorsLight.gray.divider}`,
              borderRadius: 8,
            }}
          >
            <NoResultsClipboardIcon />
            <div style={{ display: "flex", flexDirection: "column", gap: 16, opacity: 0.75 }}>
              <H1 style={{ color: colorsLight.font.light }}>No results found</H1>
              <H3 style={{ color: colorsLight.font.light }}>Try adjusting your search criteria</H3>
            </div>
          </div>
        }
      />
      <ReassignModal
        open={showReassignModal}
        setOpen={setShowReassignModal}
        reassign={handleReassign}
        from={selectedServiceCase?.assignee?.name || "Unassigned"}
        to={agent.name || ""}
        loading={reassignLoading}
      />
      <UnassignModal
        open={showUnassignModal}
        setOpen={setShowUnassignModal}
        unassign={handleUnassign}
        from={selectedServiceCase?.assignee?.name || "Unassigned"}
        loading={reassignLoading}
      />
    </div>
  );
}
